"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Sistema di registrazione mock, lato client.
 *
 * Per ora non c'è backend: l'utente "esiste" come oggetto in
 * localStorage. Quando un componente chiama `login(user)`, lo stato
 * globale viene aggiornato, il nome compare nell'header e qualunque
 * altra parte del sito può leggere `useAuth().user`.
 *
 * Quando vorremo l'OAuth vero (Google / Meta / Instagram), agganceremo
 * lo stesso `login()` al callback dei provider via Auth.js o simili —
 * tutto il resto del codice resta invariato.
 *
 * QUELLO CHE RESTA APERTO: chi è registrato vede il proprio nome in
 * ritardo.
 *
 * localStorage si legge solo nel browser, e solo dopo l'idratazione:
 * l'HTML che arriva dal server è sempre quello di un anonimo. Misurato
 * il 10/09/2026 con `auth-ritardo.js` in /tmp/pwmeas, seminando la
 * chiave e cronometrando dal navigationStart l'istante in cui il nome
 * compare nell'header: sul build di produzione servito in locale 590,
 * 710, 590 e 983 ms (desktop e telefono, due giri ciascuno), cioè da
 * 254 a 289 ms dopo il primo paint; sul dev server 1782-1947 ms, da cui
 * il "secondo e mezzo" citato nel commento di AuthMenu. Su rete e
 * dispositivo veri il vuoto è più largo di così, non più stretto.
 *
 * Lo spazio però non balla più: AuthMenu rende il bottone invisibile
 * finché `ready` è falso, quindi l'intestazione non si riassesta. Resta
 * il nome che appare tardi, e quello non si toglie dal client: qualsiasi
 * lettura sincrona di localStorage in fase di render darebbe markup
 * diverso da quello del server, cioè un errore di idratazione.
 *
 * L'unico modo per avere il menu giusto già nell'HTML è che la sessione
 * viaggi in un cookie letto sul server. Prima di farlo, sapere che:
 *
 *   · `cookies()` è una API di richiesta: usarla in un layout o in una
 *     pagina porta la rotta al rendering dinamico (doc della versione
 *     installata, 04-functions/cookies.md). Siccome AuthMenu sta nel
 *     layout di root, la pagherebbero tutte: il build di oggi
 *     prerenderizza 94 rotte, che diventerebbero 94 rese a ogni
 *     richiesta. I dati a monte non ne soffrirebbero (il `revalidate`
 *     sta sulle singole fetch, non sul segmento), ma l'HTML sì.
 *   · la via d'uscita esiste ed è `cacheComponents: true` in
 *     next.config.ts, che nella 16 accorpa ppr/useCache/dynamicIO: il
 *     guscio resta prerenderizzato e il menu, chiuso in un suo
 *     <Suspense>, arriva in streaming. È però una scelta che riguarda
 *     tutto il sito (vuole `use cache` sul resto), non questo file.
 *
 * Da rivedere insieme al passaggio di `login()` all'OAuth vero: è lì che
 * il cookie di sessione compare comunque, e la scelta va fatta una volta
 * sola.
 */

export type AuthProvider = "email" | "google" | "facebook" | "instagram";

export type AuthUser = {
  name: string;
  email: string;
  provider: AuthProvider;
};

type AuthContextValue = {
  user: AuthUser | null;
  ready: boolean;
  login: (user: AuthUser) => void;
  logout: () => void;
};

const STORAGE_KEY = "oi-auth-user-v1";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  /** `ready` = abbiamo già letto localStorage. Prima di questo,
   *  l'header non deve mostrare "Accedi" né il nome utente, perché
   *  ne uscirebbe un flash visivo. */
  const [ready, setReady] = useState(false);

  // Idratazione: leggi localStorage al mount client-side.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as AuthUser;
        if (parsed && typeof parsed.name === "string" && typeof parsed.email === "string") {
          setUser(parsed);
        }
      }
    } catch {
      // ignora errori di parsing, localStorage può essere disabilitato
    }
    setReady(true);
  }, []);

  const login = useCallback((u: AuthUser) => {
    setUser(u);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } catch {
      // localStorage può essere bloccato (es. modalità privata Safari)
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // idem
    }
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, logout }),
    [user, ready, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve essere usato dentro <AuthProvider>");
  }
  return ctx;
}
